import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckCircle2,
  Clock,
  Heart,
  ShieldCheck,
  UserRoundCheck,
  Users,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { EmptyPanel, MetricTile, PageHero, PagePanel, PageShell, PanelHeading } from "@/components/interior-page";
import Loader from "@/components/ui/loader";
import AnnouncementBanner from "@/components/AnnouncementBanner";

interface DashboardStats {
  total_users: number;
  users_by_status: Record<string, number>;
  users_by_role: Record<string, number>;
  total_listings: number;
  listings_by_status: Record<string, number>;
  total_interests: number;
}

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  under_review: "Under Review",
  active: "Active",
  under_offer: "Under Offer",
  sold: "Sold",
  expired: "Expired",
  withdrawn: "Withdrawn",
  rejected: "Rejected",
  loi: "LOI",
  due_diligence: "Due Diligence",
  escrow: "Escrow",
};

const USER_STATUS_LABELS: Record<string, string> = {
  active: "Active",
  inactive: "Inactive",
  pending_verification: "Pending",
};

export default function AdminDashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<DashboardStats>("/admin/dashboard")
      .then(setStats)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <Loader className="min-h-[60vh]" />;
  }

  if (!stats) {
    return (
      <PageShell>
        <EmptyPanel
          icon={AlertTriangle}
          title="Dashboard unavailable"
          description="The platform summary could not be loaded right now. Try refreshing the page or checking the backend connection."
        />
      </PageShell>
    );
  }

  const userStatusEntries = Object.entries(stats.users_by_status);
  const roleEntries = Object.entries(stats.users_by_role);
  const listingStatusEntries = Object.entries(stats.listings_by_status);

  return (
    <>
    <AnnouncementBanner />
    <PageShell>
      <PageHero
        eyebrow="Admin Overview"
        title="Platform health, moderation signals, and the actions that matter right now."
        description="The admin dashboard now feels like an operating surface instead of a flat stats grid. Use it to scan health, jump into moderation, and track where attention is needed across users, listings, and interest flow."
        actions={
          <>
            <Link to="/admin/listings">
              <Button size="lg" className="rounded-full">
                Review listings
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/admin/users">
              <Button size="lg" variant="outline" className="rounded-full border-primary/20 bg-white/80 text-foreground hover:bg-white">
                Manage users
              </Button>
            </Link>
          </>
        }
        aside={
          <>
            <div className="text-[0.72rem] font-bold uppercase tracking-[0.22em] text-primary/70">Snapshot</div>
            <div className="mt-3 text-3xl font-black tracking-tight text-foreground">{stats.total_listings} listings</div>
            <p className="mt-2 text-sm leading-7 text-muted-foreground">
              {stats.listings_by_status["under_review"] || 0} pending review, {stats.listings_by_status["active"] || 0} active, and {stats.total_interests} total buyer interests currently in the system.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-[1.3rem] border border-border/60 bg-white/70 p-4">
                <div className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-muted-foreground">Users</div>
                <div className="mt-2 text-2xl font-black tracking-tight text-foreground">{stats.total_users}</div>
              </div>
              <div className="rounded-[1.3rem] border border-border/60 bg-white/70 p-4">
                <div className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-muted-foreground">Interests</div>
                <div className="mt-2 text-2xl font-black tracking-tight text-foreground">{stats.total_interests}</div>
              </div>
            </div>
          </>
        }
      />

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <MetricTile label="Total Users" value={stats.total_users} icon={Users} tone="primary" href="/admin/users" />
        <MetricTile label="Total Listings" value={stats.total_listings} icon={Building2} tone="slate" href="/admin/listings" />
        <MetricTile label="Pending Review" value={stats.listings_by_status["under_review"] || 0} icon={Clock} tone="amber" href="/admin/listings" />
        <MetricTile label="Active Listings" value={stats.listings_by_status["active"] || 0} icon={CheckCircle2} tone="emerald" href="/admin/listings" />
        <MetricTile label="Buyer Interest" value={stats.total_interests} icon={Heart} tone="rose" href="/admin/interests" />
      </section>

      <section className="grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
        <PagePanel>
          <PanelHeading
            title="Users by role"
            description="See how the marketplace audience is distributed across buyers, sellers, BL Advisors, and administrators."
          />
          {roleEntries.length === 0 ? (
            <EmptyPanel
              icon={Users}
              title="No user data yet"
              description="Once people register, their role mix will show up here so you can monitor platform composition."
            />
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {roleEntries.map(([role, count]) => {
                const tone = role === "admin" ? "rose" : role === "seller" ? "emerald" : role === "bl_advisor" ? "primary" : "slate";
                return (
                  <div key={role} className="rounded-[1.4rem] border border-border/60 bg-white/70 p-4 shadow-[0_1px_0_hsl(0_0%_100%/0.82)_inset]">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className={`flex h-11 w-11 items-center justify-center rounded-[1rem] ${tone === "rose" ? "bg-rose-500/10 text-rose-600" : tone === "emerald" ? "bg-emerald-500/10 text-emerald-600" : tone === "primary" ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-700"}`}>
                          <Users className="h-4.5 w-4.5" />
                        </div>
                        <div>
                          <div className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-muted-foreground">Role</div>
                          <div className="text-sm font-bold capitalize text-foreground">{role === "bl_advisor" ? "BL Advisors" : `${role}s`}</div>
                        </div>
                      </div>
                      <div className="text-2xl font-black tracking-tight text-foreground">{count}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </PagePanel>

        <PagePanel>
          <PanelHeading
            title="Quick routes"
            description="Common admin destinations surfaced as large, readable controls."
          />
          <div className="grid gap-3">
            {[
              {
                to: "/admin/users",
                label: "User management",
                description: "Review profiles, change statuses, and manage roles.",
                icon: UserRoundCheck,
              },
              {
                to: "/admin/listings",
                label: "Listing moderation",
                description: "Approve, reject, annotate, or delete listings.",
                icon: Building2,
              },
              {
                to: "/admin/interests",
                label: "Interest inbox",
                description: "Track new buyer intent and outbound follow-up points.",
                icon: Heart,
              },
            ].map(({ to, label, description, icon: Icon }) => (
              <Link key={to} to={to} className="group rounded-[1.45rem] border border-border/60 bg-white/70 p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/20 hover:bg-white">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-[1rem] bg-primary/10 text-primary">
                      <Icon className="h-4.5 w-4.5" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-foreground">{label}</div>
                      <p className="mt-1 text-xs leading-6 text-muted-foreground">{description}</p>
                    </div>
                  </div>
                  <ArrowRight className="mt-1 h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </PagePanel>
      </section>

      <section className="grid gap-5 lg:grid-cols-[1fr_0.95fr]">
        <PagePanel>
          <PanelHeading
            title="Listings by status"
            description="A cleaner moderation snapshot across draft, review, active, sold, withdrawn, and rejected states."
          />
          {listingStatusEntries.length === 0 ? (
            <EmptyPanel
              icon={Building2}
              title="No listings yet"
              description="Listing state distribution will appear here as soon as the marketplace has inventory."
            />
          ) : (
            <div className="space-y-3">
              {listingStatusEntries.map(([statusKey, count]) => (
                <div key={statusKey} className="flex items-center justify-between rounded-[1.3rem] border border-border/60 bg-white/68 px-4 py-3">
                  <div className="flex items-center gap-3">
                    {statusKey === "active" && <CheckCircle2 className="h-4.5 w-4.5 text-emerald-600" />}
                    {statusKey === "under_review" && <Clock className="h-4.5 w-4.5 text-amber-600" />}
                    {statusKey === "draft" && <AlertTriangle className="h-4.5 w-4.5 text-slate-500" />}
                    {statusKey === "under_offer" && <ShieldCheck className="h-4.5 w-4.5 text-cyan-600" />}
                    {statusKey === "sold" && <ShieldCheck className="h-4.5 w-4.5 text-sky-600" />}
                    {statusKey === "withdrawn" && <XCircle className="h-4.5 w-4.5 text-orange-600" />}
                    {statusKey === "expired" && <XCircle className="h-4.5 w-4.5 text-zinc-600" />}
                    {statusKey === "rejected" && <XCircle className="h-4.5 w-4.5 text-rose-600" />}
                    {!["active", "under_review", "draft", "under_offer", "sold", "withdrawn", "rejected", "expired"].includes(statusKey) && <Building2 className="h-4.5 w-4.5 text-muted-foreground" />}
                    <span className="text-sm font-semibold text-foreground">{STATUS_LABELS[statusKey] || statusKey}</span>
                  </div>
                  <span className="text-xl font-black tracking-tight text-foreground">{count}</span>
                </div>
              ))}
            </div>
          )}
        </PagePanel>

        <PagePanel>
          <PanelHeading
            title="Account status mix"
            description="Keep an eye on active, inactive, and pending verification accounts."
          />
          {userStatusEntries.length === 0 ? (
            <EmptyPanel
              icon={Users}
              title="No account state data"
              description="Account status breakdown will appear here once users exist in the system."
            />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {userStatusEntries.map(([statusKey, count]) => (
                <div key={statusKey} className="rounded-[1.4rem] border border-border/60 bg-white/70 p-4 text-center">
                  <div className="text-3xl font-black tracking-tight text-foreground">{count}</div>
                  <div className="mt-2 text-[0.72rem] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    {USER_STATUS_LABELS[statusKey] || statusKey}
                  </div>
                </div>
              ))}
            </div>
          )}
        </PagePanel>
      </section>
    </PageShell>
    </>
  );
}
